// Geboortevenster · Momentvenster op een bewaard moment: het tijdstip van het moment schuift, de natal chart blijft als vaste laag.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

import { HdBodygraphChartPreset, HdBodygraphPlanetaryActivation } from '../bodygraph/hd-bodygraph.models';
import { HdJustNowTransitColorMode } from '../mandala/just-now-transit/hd-just-now-transit';
import { HdBirthWindowRenderStateFactory, createMemoizedBirthWindowEvaluator, evaluateBirthWindowSnapshot } from './birth-window-properties.builder';
import {
  HD_BIRTH_WINDOW_PROPERTY_ORDER,
  HdBirthWindowData,
  HdBirthWindowEvaluate,
  HdBirthWindowLane,
  HdBirthWindowPropertyKey,
  HdBirthWindowPropertyValue,
  HdBirthWindowSnapshot,
  HdBirthWindowTier,
} from './birth-window.models';

/**
 * Momentvenster op een bewaard moment (Steven 2026-10-25: "voor moments mag dat
 * wel"; cycle charts en composites niet, die liggen vast door de natal chart).
 *
 * Een momentchart is de natal chart met de personality-kant van het moment als
 * transitlaag. Het venster laat dus enkel het tijdstip van het moment schuiven:
 * de natal activaties blijven vast, de transitplaneten volgen de proeftijd.
 * Zoals elders voor gecombineerde charts (propertiesSummary D11) tonen de
 * Basics enkel wat de combinatie zegt: definitie, centers en kanalen. Type,
 * autoriteit, profiel, kruisen en de variabelen horen bij de persoon, niet bij
 * het moment.
 */

/** De vaste laag van het momentvenster. */
export interface HdBirthWindowOverlay {
  /** Natal activaties (beide kanten), constant over het hele venster. */
  readonly natalActivations: readonly HdBodygraphPlanetaryActivation[];
  /** Kleurmodus van de transitlaag in de chart van het paneel (zoals de chart zelf). */
  readonly colorMode: HdJustNowTransitColorMode;
}

/** Lagen van het momentvenster: geen Variabelen en Base (die gaan over het eigen design). */
export const HD_BIRTH_WINDOW_OVERLAY_TIERS: readonly HdBirthWindowTier[] = ['basics', 'planets'];

/** Eigenschappen van de combinatie natal + moment. */
export const HD_BIRTH_WINDOW_OVERLAY_PROPERTIES: ReadonlySet<HdBirthWindowPropertyKey> = new Set<HdBirthWindowPropertyKey>([
  'definition',
  'centers',
  'channels',
]);

const NONE: HdBirthWindowPropertyValue = { id: 'none', payload: { kind: 'none' } };

/** Een bewaard puntmoment met eigen activaties (een periode heeft geen vast tijdstip om te verschuiven). */
export function isHdBirthWindowMomentChart(preset: HdBodygraphChartPreset | null | undefined): boolean {
  const transit = preset?.transit;
  if (!transit || transit.momentEndUtc) return false;
  return Number.isFinite(Date.parse(transit.momentUtc)) && transit.personalityActivations.length > 0;
}

/**
 * Het venster werkt op deze preset: de natal chart (configuratie, locatie,
 * tijdzone van de klok) met het tijdstip en de activaties van het moment. Zo
 * werken venster, schatting en `/calculate` ongewijzigd.
 */
export function buildHdBirthWindowMomentPreset(preset: HdBodygraphChartPreset): HdBodygraphChartPreset | undefined {
  if (!isHdBirthWindowMomentChart(preset)) return undefined;
  const transit = preset.transit!;
  const birth = (preset.birthData ?? {}) as { time?: Record<string, unknown> | null };
  return {
    ...preset,
    birthData: { ...(preset.birthData ?? {}), time: { ...(birth.time ?? {}), utc: transit.momentUtc } },
    planetaryActivations: [...transit.personalityActivations],
    transit: undefined,
  };
}

export function hdBirthWindowOverlayOf(preset: HdBodygraphChartPreset, colorMode: HdJustNowTransitColorMode): HdBirthWindowOverlay {
  return { natalActivations: preset.planetaryActivations ?? [], colorMode };
}

/** Enkel de personality-kant: het moment is een transit, zijn designkant hoort er niet bij. */
export function toHdBirthWindowOverlayData(data: HdBirthWindowData): HdBirthWindowData {
  return { ...data, bodies: data.bodies.filter(body => body.side === 'personality') };
}

/**
 * Evaluator van het momentvenster: natal (vast) + de transitactivaties van het
 * interval, met exact dezelfde afleiding als de chart (`evaluateBirthWindowSnapshot`);
 * enkel definitie, centers en kanalen blijven. Transitactivaties dragen geen
 * `postShiftBirth`: de 19/49-regel volgt de geboorte (de natal laag), zoals in
 * `mergeTransitConfiguration`.
 */
export function createHdBirthWindowOverlayEvaluator(
  overlay: HdBirthWindowOverlay,
  createRenderState: HdBirthWindowRenderStateFactory,
): HdBirthWindowEvaluate {
  const natal = overlay.natalActivations.map(activation => ({ ...activation, id: `natal:${activation.id}` }));
  return createMemoizedBirthWindowEvaluator(activations => {
    const transit = activations.map(activation => ({ ...activation, id: `transit:${activation.id}`, postShiftBirth: undefined }));
    const snapshot = evaluateBirthWindowSnapshot([...natal, ...transit], createRenderState);
    return Object.fromEntries(
      HD_BIRTH_WINDOW_PROPERTY_ORDER.map(key => [key, HD_BIRTH_WINDOW_OVERLAY_PROPERTIES.has(key) ? snapshot[key] : NONE]),
    ) as HdBirthWindowSnapshot;
  });
}

/** Exacte transit van een bewaard moment op een (ander) tijdstip, uit `/calculate`. */
export interface HdBirthWindowMomentExact {
  /** Personality-activaties van het tijdstip (de transitlaag). */
  readonly activations: readonly HdBodygraphPlanetaryActivation[];
  /** `metadata`-blok van het document (Chiron-bron van de momentrij). */
  readonly momentMetadata: Record<string, unknown> | null;
}

/**
 * Preview-preset op de proeftijd: de venster-preset met de transitactivaties
 * van dat tijdstip. De configuratie blijft de natal chart (het paneel tekent
 * de transitlaag); `exact: false` = uit de segmenten (base en lengtegraad
 * ontbreken, het bijschrift toont "exact document volgt").
 */
export function buildHdBirthWindowMomentPreview(
  windowPreset: HdBodygraphChartPreset,
  activations: readonly HdBodygraphPlanetaryActivation[],
  exact: boolean,
): HdBodygraphChartPreset {
  return {
    ...windowPreset,
    planetaryActivations: [...activations],
    metadata: exact ? windowPreset.metadata : { ...(windowPreset.metadata ?? {}), birthWindowPreview: true },
  };
}

/** Het bewaarde moment op een nieuw tijdstip: tijdstip, transitactivaties en momentmetadata; de rest blijft. */
export function applyHdBirthWindowMomentTime(
  preset: HdBodygraphChartPreset,
  momentUtc: string,
  exact: HdBirthWindowMomentExact,
): HdBodygraphChartPreset {
  return {
    ...preset,
    transit: {
      ...preset.transit!,
      momentUtc,
      personalityActivations: [...exact.activations],
      // Nooit de metadata van het oude tijdstip laten staan (Chiron-rij).
      momentMetadata: exact.momentMetadata,
    },
  };
}

/** Enkel de lanes van het momentvenster. */
export function filterHdBirthWindowOverlayLanes(lanes: readonly HdBirthWindowLane[]): HdBirthWindowLane[] {
  return lanes.filter(lane => HD_BIRTH_WINDOW_OVERLAY_PROPERTIES.has(lane.key));
}
